import React, { createContext, useContext, useEffect, useState } from "react";
import { base44 } from "@/api/base44Client";
import Login from "@/components/Login";
import SetupWizard from "@/components/SetupWizard";
import { isLacwLocalTestMode } from "@/lib/lacwLocalTestStore";

const AuthContext = createContext(null);

const LACW_LOCAL_TEST_USER = Object.freeze({
  id: "lacw-local-test-user",
  email: "tester@example.test",
  full_name: "LACW Local Tester",
  role: "solicitor",
  portal: "lacw",
  tenant: "lacw",
  is_local_test_user: true,
});

function isHostedPortal() {
  const hostname = window.location.hostname;
  return import.meta.env.VITE_APP_INTEGRATION_MODE === "remote"
    || hostname === "portal.completelawsupport.com"
    || hostname.endsWith(".vercel.app");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings] = useState({
    id: "standalone",
    public_settings: {
      auth_required: true,
      mode: "local",
    },
  });

  useEffect(() => {
    let active = true;

    async function loadUser() {
      try {
        if (isLacwLocalTestMode()) {
          setIsInitialized(true);
          setUser(LACW_LOCAL_TEST_USER);
          setIsAuthenticated(true);
          setAuthError(null);
          return;
        }
        const isRemote = isHostedPortal();
        if (isRemote) {
          // Production must never synthesize an authenticated administrator.
          // Clear legacy browser-managed credentials and ask the server for the
          // current HTTP-only cookie session instead.
          window.localStorage.removeItem("clsp_session_token");
          window.localStorage.removeItem("deadline_guard_local_user");
          setIsInitialized(true);
          const currentUser = await base44.auth.me();
          if (!active) return;
          setUser(currentUser);
          setIsAuthenticated(Boolean(currentUser));
          setAuthError(null);
          return;
        }

        const setupInfo = await base44.setup.status();
        if (!active) return;
        setIsInitialized(setupInfo.initialized);

        if (!setupInfo.initialized) {
          setIsLoadingAuth(false);
          return;
        }

        const currentUser = await base44.auth.me();
        if (!active) return;
        if (currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
          setAuthError(null);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        if (!active) return;
        setUser(null);
        setIsAuthenticated(false);
        setAuthError({
          type: "unknown",
          message: error?.message || "Failed to load local user",
        });
      } finally {
        if (active) {
          setIsLoadingAuth(false);
        }
      }
    }

    loadUser();
    return () => {
      active = false;
    };
  }, []);

  const logout = async (shouldRedirect = true) => {
    if (isLacwLocalTestMode()) {
      setUser(LACW_LOCAL_TEST_USER);
      setIsAuthenticated(true);
      return;
    }
    setUser(null);
    setIsAuthenticated(false);
    await base44.auth.logout(shouldRedirect ? window.location.href : undefined);
  };

  const navigateToLogin = () => {
    setIsAuthenticated(false);
  };

  const checkAppState = async () => {
    if (isLacwLocalTestMode()) {
      setIsInitialized(true);
      setUser(LACW_LOCAL_TEST_USER);
      setIsAuthenticated(true);
      setAuthError(null);
      return LACW_LOCAL_TEST_USER;
    }
    const isRemote = isHostedPortal();
    if (isRemote) {
      setIsInitialized(true);
      const currentUser = await base44.auth.me();
      setUser(currentUser);
      setIsAuthenticated(Boolean(currentUser));
      return currentUser;
    }

    const setupInfo = await base44.setup.status();
    setIsInitialized(setupInfo.initialized);
    if (!setupInfo.initialized) {
      return null;
    }
    const currentUser = await base44.auth.me();
    if (currentUser) {
      setUser(currentUser);
      setIsAuthenticated(true);
      setAuthError(null);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
    return currentUser;
  };

  const handleLoginSuccess = (loggedInUser) => {
    setUser(loggedInUser);
    setIsAuthenticated(true);
    setAuthError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoadingAuth,
        isLoadingPublicSettings,
        authError,
        appPublicSettings,
        logout,
        navigateToLogin,
        checkAppState,
        setUser,
      }}
    >
      {isLoadingAuth || isInitialized === null ? (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-50">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
        </div>
      ) : !isInitialized ? (
        <SetupWizard onSetupComplete={(adminUser) => {
          setIsInitialized(true);
          handleLoginSuccess(adminUser);
        }} />
      ) : isAuthenticated && user ? (
        children
      ) : (
        <Login onLoginSuccess={handleLoginSuccess} />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
